import { inject, Injectable } from '@angular/core';
import { StorageService } from "./storage.service";
import { Router } from "@angular/router";
import { NzMessageService } from "ng-zorro-antd/message";
import { environment } from "../../environments/environment";
import { RegionService } from "./region.service";
import { MqttService } from "./mqtt.service";
import { uuidv7 } from "uuidv7";
import { isTerminalRefreshError, SupabaseAuthService } from "./supabase-auth.service";
import type {Session} from '@supabase/supabase-js';


@Injectable({
  providedIn: 'root',
})
export class AppInitService {

  private readonly storageService = inject(StorageService);
  private readonly router = inject(Router);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly regionService = inject(RegionService);
  private readonly mqttService = inject(MqttService);
  private readonly supabaseAuthService = inject(SupabaseAuthService);

  async init() {
    // 确保 installationId 存在（install_id，首次生成后不变）
    const installationId = await this.storageService.lfGet('installationId');
    if (!installationId) {
      await this.storageService.lfSet('installationId', uuidv7());
    }

    // 第三方统计不挡首屏：idle 后再动态加载 Firebase / Clarity。
    this.scheduleTelemetry();

    // URL `region` wins so a newly opened tab keeps the source page's region.
    const regionCode = this.regionService.readRegionFromLocation()
      ?? await this.regionService.getRegionCode();
    await this.regionService.setRegion(regionCode);

    // OAuth / public auth routes: do not bounce to login before the page can finish
    // exchanging the PKCE code (full-page return from Google has no session yet).
    // The environment selector must remain reachable when the current Auth
    // environment is invalid or has no session.
    if (this.isPublicAuthPath() || this.isDebugSettingRoute()) {
      return;
    }

    const version = await this.storageService.lfGet('version');
    if (!version || version !== environment.storageVersion) {
      this.nzMessageService.info(
        version ? 'New data version required, please Login.' : 'Please Login...',
        { nzDuration: 3000 },
      );
      await this.supabaseAuthService.signOutCurrentSession().catch(() => undefined);
      await this.router.navigate(['/account/login']);
      return;
    }

    let session: Session | null;
    try {
      session = await this.supabaseAuthService.getSession();
    } catch (error) {
      if (isTerminalRefreshError(error)) {
        await this.supabaseAuthService.signOutCurrentSession().catch(() => undefined);
        this.nzMessageService.info('Please Login...', { nzDuration: 3000 });
        await this.router.navigate(['/account/login']);
        return;
      }
      this.nzMessageService.error('Authentication service unavailable. Please try again.');
      return;
    }
    if (!session?.access_token) {
      this.nzMessageService.info('Please Login...', { nzDuration: 3000 });
      await this.router.navigate(['/account/login']);
      return;
    }

    await this.mqttService.init();
  }

  private scheduleTelemetry(): void {
    const run = () => {
      void this.initTelemetry();
    };
    if (typeof globalThis.requestIdleCallback === 'function') {
      globalThis.requestIdleCallback(run, { timeout: 4000 });
      return;
    }
    globalThis.setTimeout(run, 3000);
  }

  private async initTelemetry(): Promise<void> {
    if (environment.production) {
      try {
        const { default: Clarity } = await import('@microsoft/clarity');
        Clarity.init('l80rskhyp0');
      } catch (e) {
        console.log('clarity init error', e);
      }
    }

    try {
      if (environment.firebaseConfig?.apiKey) {
        const [{ initializeApp }, { getAnalytics, setUserProperties }, { getPerformance }] = await Promise.all([
          import('firebase/app'),
          import('firebase/analytics'),
          import('firebase/performance'),
        ]);
        const app = initializeApp(environment.firebaseConfig);
        const analytics = getAnalytics(app);
        setUserProperties(analytics, { app_version: environment.version });
        getPerformance(app);
      }
    } catch (e) {
      console.log('firebase init error', e);
    }
  }

  private isPublicAuthPath(): boolean {
    const path = globalThis.location?.pathname ?? '';
    return (
      path === '/auth/oauth-callback'
      || path.startsWith('/auth/oauth-callback/')
      || path === '/account/login'
      || path === '/account/register'
      || path === '/account/reset-password'
    );
  }

  private isDebugSettingRoute(): boolean {
    return globalThis.location?.pathname.replace(/\/+$/, '') === '/debug-setting';
  }
}
